:where(.label) {
  cursor: pointer;
  display: flex;
  align-items: center;
  width: fit-content;
  gap: var(--dds-spacing-x0-25);
  --dds-toggle-medium-thumb-spacing-inline: var(--dds-spacing-x0-125);
  --dds-toggle-large-thumb-spacing-inline: var(--dds-spacing-x0-25);
}

.track {
  position: relative;
  background-color: var(--dds-color-surface-medium);
  border-radius: var(--dds-border-radius-rounded);
  border: 1px solid var(--dds-color-border-default);
  @media (prefers-reduced-motion: no-preference) {
    transition:
      background-color var(--dds-motion-micro-state),
      border var(--dds-motion-micro-state),
      var(--dds-focus-transition);
  }
}

.thumb {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--dds-spacing-x0-125);
  border-radius: var(--dds-border-radius-rounded);
  background-color: var(--dds-color-surface-field-default);
  border: 1px solid var(--dds-color-border-default);
  @media (prefers-reduced-motion: no-preference) {
    transition:
      transform var(--dds-motion-micro-state),
      left var(--dds-motion-micro-state),
      border var(--dds-motion-micro-state),
      width var(--dds-motion-micro-state);
  }
}

.thumb .checkmark {
  opacity: 0;
  color: var(--dds-color-icon-action-resting);
  @media (prefers-reduced-motion: no-preference) {
    transition: opacity var(--dds-motion-micro-state);
  }
}

.thumb .unchecked-icon {
  opacity: 1;
  position: absolute;
  color: var(--dds-color-icon-medium);
  @media (prefers-reduced-motion: no-preference) {
    transition: opacity var(--dds-motion-micro-state);
  }
}

.thumb .checked-icon {
  opacity: 0;
  position: absolute;
  color: var(--dds-color-icon-medium);
  @media (prefers-reduced-motion: no-preference) {
    transition: opacity var(--dds-motion-micro-state);
  }
}

.label input:checked ~ .track > .thumb {
  .checkmark {
    opacity: 1;
  }
  .checked-icon {
    opacity: 1;
  }
  .unchecked-icon {
    opacity: 0;
  }
}

.labeltext--readonly {
  display: flex;
  .icon--read-only {
    align-self: center;
    flex-shrink: 0;
    margin-inline-end: var(--dds-spacing-x0-125);
  }
}

.medium {
  .track {
    height: var(--dds-spacing-x1-5);
    width: 2.8125rem;
  }

  .thumb {
    left: var(--dds-toggle-medium-thumb-spacing-inline);
    top: 1px;
    height: 1.25rem;
    width: 1.25rem;
  }

  input:checked ~ .track > .thumb {
    left: calc(100% - var(--dds-toggle-medium-thumb-spacing-inline));
    transform: translateX(-100%);
  }

  input:enabled:active:enabled:not([aria-readonly='true']) ~ .track > .thumb {
    width: 1.5rem;
  }
}

.large {
  .track {
    height: 2.125rem;
    width: 3.25rem;
  }

  .thumb {
    left: var(--dds-toggle-large-thumb-spacing-inline);
    top: var(--dds-spacing-x0-125);
    height: 1.75rem;
    width: 1.75rem;
  }

  input:checked ~ .track > .thumb {
    left: calc(100% - var(--dds-toggle-large-thumb-spacing-inline));
    transform: translateX(-100%);
  }

  input:enabled:active:not([aria-readonly='true']) ~ .track > .thumb {
    width: var(--dds-spacing-x2);
  }
}

.label,
.label--is-loading {
  input:checked:enabled:not([aria-readonly='true']) ~ .track {
    background-color: var(--dds-color-surface-action-selected);
    border-color: var(--dds-color-border-action-default);

    & > .thumb {
      border-color: var(--dds-color-border-action-default);
    }
  }
}

.label:hover {
  input:enabled:checked:not([aria-readonly='true']) ~ .track {
    background-color: var(--dds-color-surface-action-hover);
    border-color: var(--dds-color-border-action-hover);

    .thumb {
      border-color: var(--dds-color-border-action-hover);
    }
  }
  input:enabled:not([aria-readonly='true']) ~ .track {
    background-color: var(--dds-color-surface-hover-default);
    border-color: var(--dds-color-border-action-hover);

    .thumb {
      border-color: var(--dds-color-border-action-hover);

      .checkmark {
        color: var(--dds-color-icon-action-hover);
      }
    }
  }
}

.label--color-scheme,
.label--color-scheme:hover {
  input:enabled:not([aria-readonly='true']) ~ .track,
  input:checked:enabled:not([aria-readonly='true']) ~ .track {
    background-color: var(--dds-color-surface-medium);
    border-color: var(--dds-color-border-default);
    .thumb {
      border-color: var(--dds-color-border-default);
    }
  }
}

.label--disabled {
  cursor: not-allowed;
  color: var(--dds-color-text-subtle);

  input:disabled ~ .track {
    background-color: var(--dds-color-surface-field-disabled);
  }

  input:checked:disabled ~ .track {
    background-color: var(--dds-color-surface-action-selected-disabled);
    border-color: var(--dds-color-surface-action-selected-disabled);
  }

  input:checked:disabled ~ .track > .thumb {
    border-color: var(--dds-color-surface-action-selected-disabled);

    .checkmark {
      color: var(--dds-color-icon-subtle);
    }
    .checked-icon {
      color: var(--dds-color-icon-subtle);
    }
    .unchecked-icon {
      color: var(--dds-color-icon-subtle);
    }
  }
}

.label--read-only {
  cursor: default;
  color: var(--dds-color-text-medium);

  input ~ .track {
    background-color: var(--dds-color-surface-default);
  }

  input:checked ~ .track {
    background-color: var(--dds-color-surface-inverse-default);
    border-color: var(--dds-color-surface-inverse-default);
  }

  input:checked ~ .track > .thumb {
    border-color: var(--dds-color-surface-inverse-default);
    .checkmark {
      color: var(--dds-color-icon-medium);
    }
  }
}

.label--is-loading {
  cursor: not-allowed;
}
